import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getMothers } from './api';

const AllMothers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mothers, setMothers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMother, setSelectedMother] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMothers();
        // Ensure data is an array
        const mothersArray = Array.isArray(data) ? data : (data?.mothers || data?.data || []);
        setMothers(mothersArray);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalMothers = mothers.length;
  const districtMothersCount = mothers.reduce((acc, mother) => {
    acc[mother.location || 'Unknown'] = (acc[mother.location || 'Unknown'] || 0) + 1;
    return acc;
  }, {});
  const topDistrict = Object.entries(districtMothersCount).reduce((max, curr) =>
    max[1] > curr[1] ? max : curr, ['Unknown', 0]);
  const avgCompliance = mothers.length > 0
    ? (mothers.reduce((sum, m) => sum + (parseInt(m.compliance_score?.split('/')[0] || 0) / parseInt(m.compliance_score?.split('/')[1] || 1) * 100), 0) / mothers.length).toFixed(2)
    : 0;

  const handleViewMother = (mother) => setSelectedMother(mother);
  const handleCloseDialog = () => setSelectedMother(null);

  const StatCard = ({ title, value, icon, color, description }) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(255,105,180,0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: '#f5f5f5', minHeight: '100vh', width: '100%' }}>
      

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 8,p:8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            p:4,  
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <StatCard 
              title="Total Mothers" 
              value={totalMothers} 
              icon={<PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Registered across all partners"
            />
            <StatCard 
              title="Top District" 
              value={topDistrict[1]} 
              icon={<LocationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description={`${topDistrict[0]}`}
            />
            <StatCard 
              title="Avg Compliance" 
              value={`${avgCompliance}%`} 
              icon={<CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />} 
              color="linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
              description="Meeting standards"
            />
          </Box>

          <Grid container spacing={3}>
            {mothers.map((mother) => (
              <Grid item xs={12} sm={6} md={4} key={mother.generated_id}>
                <Card
                  sx={{
                    p: 3,
                    background: '#f92955',
                    borderRadius: 3,
                    boxShadow: '0 6px 30px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': { transform: 'scale(1.03)', boxShadow: '0 8px 36px rgba(0,0,0,0.12)' },
                  }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                    <Avatar
                      src="/path/to/woman-avatar.jpg"
                      sx={{ width: 100, height: 100, border: '5px solid #fff', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}
                    />
                    <Typography variant="h6" sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>{mother.first_name} {mother.surname}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}>Children: {mother.num_children || 0}</Typography>
                    <Button
                      variant="contained"
                      onClick={() => handleViewMother(mother)}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      View Mother
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Drawer
            anchor="right"
            open={!!selectedMother}
            onClose={handleCloseDialog}
            PaperProps={{
              sx: {
                width: isMobile ? '100%' : '40%',
                maxWidth: '100vw',
                height: '100vh',
                background: '#f92955',
                boxShadow: '-10px 0 30px rgba(0,0,0,0.2)',
              }
            }}
          >
            {selectedMother && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h5" sx={{ color: '#1a2e5c', fontWeight: 'bold' }}>Mother Profile</Typography>
                  <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                  <Avatar
                    src="/path/to/woman-avatar.jpg"
                    sx={{ width: 150, height: 150, border: '6px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                  />
                  <Box sx={{ mt: 3, width: '100%' }}>
                    <Typography variant="h6" sx={{ color: '#1a2e5c' }}><strong>ID:</strong> {selectedMother.generated_id}</Typography>
                    <Typography variant="h6" sx={{ color: '#1a2e5c' }}><strong>Name:</strong> {selectedMother.first_name} {selectedMother.surname}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Contact:</strong> {selectedMother.mobile_number || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Children:</strong> {selectedMother.num_children || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Ages of Children:</strong> {selectedMother.ages_of_children ? selectedMother.ages_of_children.join(', ') : 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Location:</strong> {selectedMother.location || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Education Level:</strong> {selectedMother.education_level || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>NIN:</strong> {selectedMother.nin || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Partner ID:</strong> {selectedMother.partner_id || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Donor ID:</strong> {selectedMother.donor_id || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Activity Points:</strong> {selectedMother.activity_points || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Total Savings:</strong> ${selectedMother.savings || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Monthly Savings:</strong> ${selectedMother.total_monthly_savings || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Milestone Score:</strong> {selectedMother.milestone_score || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Compliance Score:</strong> {selectedMother.compliance_score || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Donor Contributions:</strong> ${selectedMother.donor_contributions || 0}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Created At:</strong> {selectedMother.created_at ? new Date(selectedMother.created_at).toLocaleDateString() : 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}><strong>Updated At:</strong> {selectedMother.updated_at ? new Date(selectedMother.updated_at).toLocaleDateString() : 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555', mt: 2 }}><strong>Donor Profile:</strong></Typography>
                    {selectedMother.donor_profile ? (
                      <Box sx={{ pl: 2, mb: 1, borderLeft: '2px solid #2a9d8f' }}>
                        <Typography variant="body2" sx={{ color: '#1a2e5c' }}><strong>Name:</strong> {selectedMother.donor_profile.first_name} {selectedMother.donor_profile.surname}</Typography>
                        <Typography variant="body2" sx={{ color: '#1a2e5c' }}><strong>Email:</strong> {selectedMother.donor_profile.email}</Typography>
                        <Typography variant="body2" sx={{ color: '#1a2e5c' }}><strong>Country:</strong> {selectedMother.donor_profile.country_of_residence || 'N/A'}</Typography>
                        <Typography variant="body2" sx={{ color: '#1a2e5c' }}><strong>Total Contributions:</strong> ${selectedMother.donor_profile.total_contributions || 0}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#777' }}>No donor assigned</Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#555', mt: 2 }}><strong>Activities:</strong></Typography>
                    {selectedMother.activities && selectedMother.activities.length > 0 ? (
                      selectedMother.activities.map((activity) => (
                        <Box key={activity.activity_id} sx={{ pl: 2, mb: 1, borderLeft: '2px solid #2a9d8f' }}>
                          <Typography variant="body2" sx={{ color: '#1a2e5c' }}>{activity.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#777' }}>{activity.description}</Typography>
                          <Typography variant="caption" sx={{ color: '#777' }}>Participants: {activity.num_people}</Typography>
                          <Typography variant="caption" sx={{ color: '#777' }}>Created: {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : 'N/A'}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: '#777' }}>No activities recorded</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Drawer>
        </>
      )}
    </Box>
  );
};

export default AllMothers;